import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { hämtaDemostatus, hämtaFortnoxInloggning, hämtaHändelse, hämtaHändelselogg, hämtaSystemstatus, LoggRad, Systemstatus } from '../api/client';
import SokbarLista from '../components/SokbarLista';
import InstallningarFlikar from '../components/InstallningarFlikar';
import { TEMA } from '../theme';

// Läsbara namn på händelsetyperna i filtret.
const HÄNDELSER: Record<string, string> = {
  aktivitet_skapad: 'Aktivitet skapad',
  aktivitet_kopplad: 'Aktivitet kopplad',
  aktivitet_avbockad: 'Aktivitet avbockad',
  aktivitet_ändrad: 'Aktivitet ändrad',
  aktivitet_borttagen: 'Aktivitet borttagen',
  kund_skapad: 'Kund skapad',
  kund_ändrad: 'Kund ändrad',
  inkopare_skapad: 'Inköpare tillagd',
  inkopare_borttagen: 'Inköpare borttagen',
  vin_utgaende: 'Vin utgående',
  vin_ej_utgaende: 'Vin inte längre utgående',
  vin_bekraftat: 'Vin bekräftat',
  kunder_sammanslagna: 'Kunder sammanslagna',
  inte_dubbletter: 'Inte dubbletter',
  anvandare_skapad: 'Användare skapad',
  anvandare_andrad: 'Användare ändrad',
  anvandare_aktiverad: 'Användare aktiverad',
  anvandare_inaktiverad: 'Användare inaktiverad',
  anvandare_borttagen: 'Gäst borttagen',
  losenordslank_skickad: 'Lösenordslänk skickad',
  motesmejl: 'Mötesmejl inläst',
  motesmejl_fel: 'Mötesmejl kunde inte läsas in',
  motesmejl_forsok_igen: 'Mötesmejl inläst på nytt',
  motesmejl_hanterat: 'Mötesmejl markerat som hanterat',
  motesintag_larm: 'Mötesintaget har stannat',
  motesintag_ok: 'Mötesintaget fungerar igen',
  // Inköp
  inkop_skapat: 'Inköp skapat',
  inkop_andrat: 'Inköp ändrat',
  inkop_status: 'Inköp bytte status',
  inkop_borttaget: 'Inköp borttaget',
  inkopsrad_skapad: 'Inköpsrad tillagd',
  inkopsrad_andrad: 'Inköpsrad ändrad',
  inkopsrad_borttagen: 'Inköpsrad borttagen',
  inkopsrad_kopplad: 'Inköpsrad kopplad till artikel',
  inkopsrad_inte_samma: 'Artikelförslag avvisat',
  inkopsrad_utan_artikel: 'Inköpsrad utan artikel',
  leverans_skapad: 'Leverans skapad',
  leverans_andrad: 'Leverans ändrad',
  leverans_borttagen: 'Leverans borttagen',
  kostnad_skapad: 'Kostnad tillagd',
  kostnad_andrad: 'Kostnad ändrad',
  kostnad_borttagen: 'Kostnad borttagen',
  betalning_skapad: 'Betalning tillagd',
  inkop_betalning: 'Betalning ändrad',
  betalning_borttagen: 'Betalning borttagen',
  betalningar_foreslagna: 'Betalningar föreslagna',
  producent_skapad: 'Producent skapad',
  producent_andrad: 'Producent ändrad',
  kandidat_blev_producent: 'Kandidat blev producent',
  uppgift_skapad: 'Att göra tillagt (inköp)',
  uppgift_klar: 'Att göra avbockat (inköp)',
  uppgift_andrad: 'Att göra ändrat (inköp)',
  uppgift_borttagen: 'Att göra borttaget (inköp)',
  lagesrapport_inlast: 'Läglistan inläst',
  kandidat_steg: 'Kandidat bytte steg',
  prover_registrerade: 'Prover registrerade',
  prov_andrat: 'Prov ändrat',
  prov_borttaget: 'Prov borttaget',
  prov_omdome: 'Omdöme på prov',
  prov_omdome_borttaget: 'Omdöme borttaget',
  kontakt_skapad: 'Producentkontakt tillagd',
  kontakt_andrad: 'Producentkontakt ändrad',
  kontakt_borttagen: 'Producentkontakt borttagen',
  producentanteckning: 'Producentanteckning',
  producentanteckning_borttagen: 'Producentanteckning borttagen',
  kategori_skapad: 'Kategori skapad',
  kategori_andrad: 'Kategori ändrad',
  kategori_borttagen: 'Kategori borttagen',
  plan_skapad: 'Inköpsplan: tillagd',
  plan_andrad: 'Inköpsplan: ändrad',
  plan_borttagen: 'Inköpsplan: borttagen',
  inkop_installningar: 'Kalkylinställningar ändrade',
  tillverkare_kopplad: 'Tillverkare kopplad till producent',
  tillverkare_ignorerad: 'Tillverkare utan producent',
  inkop_inte_dubblett: 'Inte dubbletter (inköp)',
  avvikelse_besvarad: 'Att åtgärda besvarad',
  import_prismodell: 'Prismodellen importerad',
};

const MODULFILTER: { värde: string; etikett: string }[] = [
  { värde: '', etikett: 'Alla moduler' },
  { värde: 'inkop', etikett: 'Inköp' },
];

// Ett värde i "före → efter". Listor och objekt sammanfattas.
function värdeText(v: unknown): string {
  if (v === null || v === undefined || v === '') return '–';
  if (typeof v === 'boolean') return v ? 'ja' : 'nej';
  if (Array.isArray(v)) return v.length ? v.map(x => (typeof x === 'object' ? '…' : String(x))).join(', ') : '–';
  if (typeof v === 'object') return `${Object.keys(v as object).length} st`;
  return String(v);
}

function Ändringar({ andrat }: { andrat: Record<string, [unknown, unknown]> }) {
  return (
    <span style={{ display: 'block', marginTop: '3px', color: TEMA.inkFaint, fontSize: '12.5px' }}>
      {Object.entries(andrat).map(([fält, [före, efter]], n) => (
        <span key={fält}>
          {n > 0 && ' · '}
          {fält}: {värdeText(före)} → {värdeText(efter)}
        </span>
      ))}
    </span>
  );
}

// Det som sparades med händelsen, till exempel allt som fanns på ett borttaget inköp.
function Detaljer({ id }: { id: number }) {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [öppen, setÖppen] = useState(false);
  return (
    <span style={{ display: 'block', marginTop: '3px' }}>
      <button
        onClick={() => {
          setÖppen(o => !o);
          if (!data) hämtaHändelse(id).then(r => setData(r.data.data)).catch(() => setData({ fel: 'Kunde inte hämtas' }));
        }}
        style={{ background: 'none', border: 'none', padding: 0, color: TEMA.inkFaint, cursor: 'pointer', fontSize: '12.5px', fontFamily: "'Inter', sans-serif" }}
      >
        {öppen ? '▾' : '▸'} Det som sparades
      </button>
      {öppen && (
        <pre style={{
          margin: '6px 0 0', padding: '10px', background: TEMA.bgPanel, border: `1px solid ${TEMA.border}`,
          borderRadius: '3px', fontSize: '11.5px', color: TEMA.inkSoft, maxHeight: '320px', overflow: 'auto', whiteSpace: 'pre-wrap',
        }}>
          {data ? JSON.stringify(data, null, 2) : 'Hämtar...'}
        </pre>
      )}
    </span>
  );
}

// "i dag 14:05", "i går 09:12" eller "3 okt 14:05".
function senastText(iso: string | null | undefined): string {
  if (!iso) return 'aldrig';
  const d = new Date(iso);
  const tid = d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  const igår = new Date(); igår.setDate(igår.getDate() - 1);
  if (d.toDateString() === new Date().toDateString()) return `i dag ${tid}`;
  if (d.toDateString() === igår.toDateString()) return `i går ${tid}`;
  return `${d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })} ${tid}`;
}

function StatusRad({ namn, s, maxMinuter }: { namn: string; s?: Systemstatus; maxMinuter: number }) {
  const senast = s?.senast_ok ? new Date(s.senast_ok) : null;
  const ok = !!senast && (Date.now() - senast.getTime()) / 60000 <= maxMinuter;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
      <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: ok ? TEMA.accent : '#D9695F' }} />
      <span style={{ color: TEMA.inkSoft }}>{namn}</span>
      <span style={{ color: ok ? TEMA.inkFaint : '#D9695F' }} title={s?.senaste_fel || undefined}>
        senast OK {senastText(s?.senast_ok)}
      </span>
    </span>
  );
}

const PERIODER = [7, 30, 90];

function dagRubrik(d: Date): string {
  const idag = new Date();
  const igår = new Date(); igår.setDate(idag.getDate() - 1);
  if (d.toDateString() === idag.toDateString()) return 'I dag';
  if (d.toDateString() === igår.toDateString()) return 'I går';
  const text = d.toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' });
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function toggleStil(aktiv: boolean): React.CSSProperties {
  return {
    padding: '7px 14px', borderRadius: '2px', fontFamily: "'Inter', sans-serif", fontSize: '13px',
    fontWeight: 500, cursor: 'pointer',
    border: `1px solid ${aktiv ? TEMA.accent : TEMA.border}`,
    background: aktiv ? TEMA.accent : 'transparent',
    color: aktiv ? TEMA.onAccent : TEMA.inkSoft,
  };
}

export default function Handelselogg() {
  const [dagar, setDagar] = useState(7);
  const [rader, setRader] = useState<LoggRad[] | null>(null);
  const [fel, setFel] = useState<string | null>(null);
  const [person, setPerson] = useState('');
  const [typ, setTyp] = useState('');
  const [modul, setModul] = useState('');
  const [status, setStatus] = useState<Record<string, Systemstatus> | null>(null);

  // I demon är mötesintaget och synkarna avstängda med flit. Statusraden skulle
  // bara visa röda prickar, så den hämtas och visas inte där.
  useEffect(() => {
    let aktuell = true;
    hämtaDemostatus()
      .then(r => !!r.data.demo)
      .catch(() => false)
      .then(demo => {
        if (demo || !aktuell) return;
        hämtaSystemstatus().then(r => { if (aktuell) setStatus(r.data); }).catch(() => { /* statusen är en hjälp */ });
      });
    return () => { aktuell = false; };
  }, []);

  useEffect(() => {
    let avbruten = false;
    setRader(null);
    setFel(null);
    hämtaHändelselogg(dagar, modul)
      .then(r => { if (!avbruten) setRader(r.data); })
      .catch(err => {
        console.error('Kunde inte ladda händelseloggen:', err);
        if (!avbruten) setFel('Kunde inte ladda händelseloggen just nu. Försök igen om en stund.');
      });
    return () => { avbruten = true; };
  }, [dagar, modul]);

  const personer = useMemo(
    () => Array.from(new Set((rader || []).map(r => r.vem || 'Systemet'))).sort((a, b) => a.localeCompare(b, 'sv')),
    [rader],
  );
  const typer = useMemo(
    () => Array.from(new Set((rader || []).map(r => r.handelse)))
      .sort((a, b) => (HÄNDELSER[a] || a).localeCompare(HÄNDELSER[b] || b, 'sv')),
    [rader],
  );

  const synliga = (rader || []).filter(r =>
    (!person || (r.vem || 'Systemet') === person) && (!typ || r.handelse === typ));

  // Raderna kommer nyast först, så en ny grupp börjar varje gång dagen byts.
  const grupper: { rubrik: string; rader: LoggRad[] }[] = [];
  synliga.forEach(r => {
    const rubrik = dagRubrik(new Date(r.tid));
    const sista = grupper[grupper.length - 1];
    if (sista && sista.rubrik === rubrik) sista.rader.push(r);
    else grupper.push({ rubrik, rader: [r] });
  });

  return (
    <div>
      <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: '26px', color: TEMA.ink, marginBottom: '24px' }}>Inställningar</h1>
      <InstallningarFlikar />
      <div style={{ marginBottom: '24px' }}>
        <p style={{ margin: 0, fontSize: '13.5px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif", maxWidth: '680px', lineHeight: 1.5 }}>
          Vem som kopplat, skapat, ändrat, bekräftat eller slagit ihop vad, och när. Mejl som
          mötesintaget läst in står på säljaren som skickade dem.
        </p>
        {status && (
          <div style={{
            display: 'flex', gap: '8px 24px', flexWrap: 'wrap', marginTop: '14px',
            fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px',
          }}>
            <StatusRad namn="Mötesintag" s={status.mote_intag} maxMinuter={30} />
            <StatusRad namn="Ordersynk" s={status.ordersynk} maxMinuter={45} />
            <StatusRad namn="Fakturasynk" s={status.fakturasynk} maxMinuter={45} />
            <StatusRad namn="Leverantörsfakturor" s={status.leverantorsfakturor} maxMinuter={75} />
            {/* När Vinsales får nya behörigheter i Fortnox måste en admin godkänna dem där.
                Knappen syns bara när leverantörssynken inte fungerar. */}
            {(!status.leverantorsfakturor?.senast_ok || !!status.leverantorsfakturor?.senaste_fel) && <button
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: TEMA.accent, fontFamily: 'inherit', fontSize: 'inherit' }}
              onClick={() => hämtaFortnoxInloggning().then(r => { window.location.href = r.data.url; })
                .catch(() => window.alert('Kunde inte starta inloggningen mot Fortnox.'))}
            >Godkänn behörigheter i Fortnox →</button>}
            {/* Hämtas var tredje timme från Riksbanken. */}
            <StatusRad namn="Valutakurser" s={status.valutakurser} maxMinuter={6 * 60} />
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '22px' }}>
        <div style={{ display: 'flex', gap: '6px' }}>
          {PERIODER.map(d => (
            <button key={d} onClick={() => setDagar(d)} style={toggleStil(dagar === d)}>
              {d} dagar
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          {MODULFILTER.map(m => (
            <button key={m.värde} onClick={() => { setModul(m.värde); setTyp(''); }} style={toggleStil(modul === m.värde)}>
              {m.etikett}
            </button>
          ))}
        </div>
        <div style={{ width: '200px' }}>
          <SokbarLista
            värde={person}
            onChange={setPerson}
            placeholder="Alla personer"
            alternativ={personer.map(p => ({ värde: p, etikett: p }))}
          />
        </div>
        <div style={{ width: '240px' }}>
          <SokbarLista
            värde={typ}
            onChange={setTyp}
            placeholder="Alla händelser"
            alternativ={typer.map(t => ({ värde: t, etikett: HÄNDELSER[t] || t }))}
          />
        </div>
        {rader && (
          <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px', color: TEMA.inkFaint, marginLeft: 'auto' }}>
            {synliga.length} {synliga.length === 1 ? 'händelse' : 'händelser'} senaste {dagar} dagarna
          </span>
        )}
      </div>

      {fel && <p style={{ color: '#D9695F', fontSize: '14px', fontFamily: "'Inter', sans-serif" }}>{fel}</p>}
      {!fel && rader === null && (
        <p style={{ color: TEMA.inkFaint, fontSize: '14px', fontFamily: "'Inter', sans-serif" }}>Laddar...</p>
      )}
      {rader && synliga.length === 0 && (
        <p style={{ color: TEMA.inkFaint, fontSize: '14px', fontFamily: "'Inter', sans-serif" }}>
          Inga händelser under perioden{person || typ ? ' med de valda filtren' : ''}.
        </p>
      )}

      {grupper.map(g => (
        <div key={g.rubrik} style={{ marginBottom: '26px' }}>
          <p style={{
            margin: '0 0 6px', fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px',
            letterSpacing: '0.08em', textTransform: 'uppercase', color: TEMA.accent,
          }}>
            {g.rubrik}
          </p>
          <div style={{ borderTop: `1px solid ${TEMA.border}` }}>
            {g.rader.map(r => (
              <div key={r.id} style={{
                display: 'flex', gap: '16px', alignItems: 'baseline', flexWrap: 'wrap',
                padding: '10px 0', borderBottom: `1px solid ${TEMA.border}`,
                fontFamily: "'Inter', sans-serif", fontSize: '13.5px',
              }}>
                <span style={{ color: TEMA.inkFaint, width: '44px', fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px' }}>
                  {new Date(r.tid).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                </span>
                <span style={{ color: TEMA.inkSoft, width: '80px' }}>{r.vem || 'Systemet'}</span>
                <span style={{ color: TEMA.ink, flex: 1, minWidth: '240px' }}>
                  {r.beskrivning}
                  {r.andrat && Object.keys(r.andrat).length > 0 && <Ändringar andrat={r.andrat} />}
                  {r.har_detaljer && <Detaljer id={r.id} />}
                </span>
                {r.inkop_id && (
                  <Link to={`/inkop/lista/${r.inkop_id}`} style={{ color: TEMA.accent, fontSize: '12.5px', textDecoration: 'none' }}>Inköpet →</Link>
                )}
                {!r.inkop_id && r.producent_id && r.producent_namn && (
                  <Link to={`/inkop/producenter/${r.producent_id}`} style={{ color: TEMA.accent, fontSize: '12.5px', textDecoration: 'none' }}>{r.producent_namn} →</Link>
                )}
                {r.kund_id && (
                  r.kund_namn
                    ? <Link to={`/kund/${r.kund_id}`} style={{ color: TEMA.accent, fontSize: '12.5px', textDecoration: 'none' }}>{r.kund_namn} →</Link>
                    : <span style={{ color: TEMA.inkFaint, fontSize: '12.5px' }}>borttagen kund</span>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
